import { useCallback, useMemo, useState } from 'react';

import { TiendaPerfectaVisitHistoryStop } from 'models/TiendaPerfecta';

import {
  RouteExecution,
  buildRouteExecutions,
  initialVisibleKeys,
} from '../utils/mapData';
import { VisitSelection } from '../utils/selection';
import VisitMap from './VisitMap';
import VisitRoutesPanel from './VisitRoutesPanel';

interface VisitMapAndRoutesProps {
  stops: TiendaPerfectaVisitHistoryStop[];
  /**
   * Identity of the query (range, zones, distributors). Route visibility is
   * kept across data refreshes of the same query and reset when it changes.
   */
  queryKey: string;
  /** The Routes KPI of the range, for the panel title. */
  routesTotal: number;
  selection: VisitSelection;
  /** Toggling setter of the shared selection. */
  onSelect: (selection: NonNullable<VisitSelection>) => void;
  timeZone: string;
}

const NO_OVERRIDES: ReadonlyMap<string, boolean> = new Map();

/**
 * The map and the routes panel side by side, sharing which route executions
 * are visible. Visibility is local to this block; the selection is the
 * screen's.
 */
function VisitMapAndRoutes({
  stops,
  queryKey,
  routesTotal,
  selection,
  onSelect,
  timeZone,
}: VisitMapAndRoutesProps) {
  const executions = useMemo(() => buildRouteExecutions(stops), [stops]);

  // What the user changed, by route-execution key (routeId + date), for one
  // query. A refresh of the same query builds new stops and executions but
  // keeps these; a route that appears later follows the initial rule.
  const [stored, setStored] = useState<{
    queryKey: string;
    overrides: ReadonlyMap<string, boolean>;
  }>({ queryKey, overrides: NO_OVERRIDES });
  const overrides =
    stored.queryKey === queryKey ? stored.overrides : NO_OVERRIDES;

  const initial = useMemo(() => initialVisibleKeys(executions), [executions]);
  const visibleKeys = useMemo(
    () =>
      new Set(
        executions
          .filter((e) => overrides.get(e.key) ?? initial.has(e.key))
          .map((e) => e.key)
      ),
    [executions, initial, overrides]
  );

  const setOverrides = useCallback(
    (
      update: (
        current: ReadonlyMap<string, boolean>
      ) => ReadonlyMap<string, boolean>
    ) =>
      setStored((prev) => ({
        queryKey,
        overrides: update(
          prev.queryKey === queryKey ? prev.overrides : NO_OVERRIDES
        ),
      })),
    [queryKey]
  );

  const setVisible = useCallback(
    (keys: string[], visible: boolean) =>
      setOverrides((current) => {
        const next = new Map(current);
        keys.forEach((key) => next.set(key, visible));

        return next;
      }),
    [setOverrides]
  );

  const toggle = useCallback(
    (key: string) => setVisible([key], !visibleKeys.has(key)),
    [setVisible, visibleKeys]
  );

  const showAll = useCallback(
    () =>
      setVisible(
        executions.map((e) => e.key),
        true
      ),
    [setVisible, executions]
  );

  const selectRoute = useCallback(
    (execution: RouteExecution) => {
      const alreadySelected =
        selection?.kind === 'route' &&
        selection.routeId === execution.routeId &&
        selection.date === execution.date;
      // A route picked from the list is always shown: selecting one the map
      // is hiding would focus on nothing.
      if (!alreadySelected && !visibleKeys.has(execution.key)) {
        setVisible([execution.key], true);
      }
      onSelect({
        kind: 'route',
        routeId: execution.routeId,
        date: execution.date,
      });
    },
    [selection, visibleKeys, setVisible, onSelect]
  );

  return (
    <div className="visit-history-maprow">
      <VisitMap
        executions={executions}
        visibleKeys={visibleKeys}
        selection={selection}
        timeZone={timeZone}
      />
      <VisitRoutesPanel
        executions={executions}
        total={routesTotal}
        visibleKeys={visibleKeys}
        selection={selection}
        onToggle={toggle}
        onShowAll={showAll}
        onSelectRoute={selectRoute}
      />
    </div>
  );
}

export default VisitMapAndRoutes;
